import React, {
  useState,
  useMemo,
  memo,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import {
  IcoMoon, ArrowDownIcon,
} from '@usap/uicomponents'
import SelectNoFocus from './style/SelectBox.css'
import SelectBoxTitle from './SelectBoxTitle'

const SelectBox = ({
  index,
  text,
  open,
  dropHeight,
  options,
  disabled,
  selectValue,
  overlay,
  resetDefault,
  onChangeHandler,
  element,
  onKeyUp,
  selectBoxWidth,
  selectBoxMinWidth,
  onOpen,
}) => {
  const [availableOptions, setAvailableOptions] = useState(options)
  const [loading, setLoading] = useState(false)
  const initializing = useRef(false)
  const optionsToDisplay = useMemo(
    () => (options.length && options) ||
      (availableOptions.length && availableOptions) ||
      [selectValue],
    [availableOptions, options, selectValue],
  )

  const dropDownProps = useMemo(
    () => ({
      width: selectBoxWidth,
      minWidth: selectBoxMinWidth,
      margin: { top: '3px' },
    }),
    [selectBoxMinWidth, selectBoxWidth],
  )

  const dropDrownHeight = useMemo(
    () => (optionsToDisplay.length > 5 ? dropHeight : `${optionsToDisplay.length * 54}px`),
    [dropHeight, optionsToDisplay],
  )

  const onOpenCallback = useCallback(() => {
    if (initializing.current) return

    document.addEventListener('keyup', onKeyUp, true)

    if (!overlay) resetDefault(true)

    if (onOpen && optionsToDisplay.length < 2) {
      setLoading(true)
      initializing.current = true

      onOpen(data => {
        setAvailableOptions(data)
        setLoading(false)
      })
    }
  }, [onKeyUp, onOpen, optionsToDisplay, overlay, resetDefault])

  useEffect(() => {
    if (loading) {
      setAvailableOptions([''])
    } else {
      initializing.current = false
    }
  }, [loading])

  const onChangeAction = useCallback(selectedValue => {
    onChangeHandler(element, selectedValue)
  }, [element, onChangeHandler])

  const onCloseAction = useCallback(() => {
    document.removeEventListener('keyup', onKeyUp, true)
  }, [onKeyUp])

  const icon = useMemo(() => <IcoMoon icon={ArrowDownIcon} size="24px" />, [])
  const disableSelect = useMemo(() => (loading ? [0] : disabled), [disabled, loading])

  return (
    <SelectNoFocus
      a11yTitle={`${index}`}
      ariaLabel={`${index}`}
      id={`select-${index}`}
      outline="false"
      focusIndicator={false}
      key={`select-${index}-${open}`}
      open={open}
      dropProps={dropDownProps}
      options={optionsToDisplay}
      disabled={disableSelect}
      value={selectValue}
      placeholder={text}
      dropHeight={dropDrownHeight}
      icon={icon}
      onOpen={onOpenCallback}
      onClose={onCloseAction}
      onChange={onChangeAction}
    >
      {option => (
        <SelectBoxTitle
          option={option}
          loading={loading}
          index={index}
          key={option}
        />
      )}
    </SelectNoFocus>
  )
}

export default memo(SelectBox)
